import { useState, useEffect } from 'react';

export type ThemeMode = 'auto' | 'light' | 'dark';
type ResolvedThemeMode = Exclude<ThemeMode, 'auto'>;

const STORAGE_KEY = 'prismancy-theme';
const DARK_MODE_QUERY = '(prefers-color-scheme: dark)';

const isThemeMode = (mode: string | null): mode is ThemeMode => {
  return mode === 'auto' || mode === 'light' || mode === 'dark';
};

export const getSystemTheme = (): ResolvedThemeMode => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia(DARK_MODE_QUERY).matches ? 'dark' : 'light';
};

export const getStoredThemeMode = (): ThemeMode => {
  if (typeof localStorage === 'undefined') return 'auto';

  const storedMode = localStorage.getItem(STORAGE_KEY);
  return isThemeMode(storedMode) ? storedMode : 'auto';
};

export const resolveThemeMode = (mode: ThemeMode): ResolvedThemeMode => {
  return mode === 'auto' ? getSystemTheme() : mode;
};

export const applyTheme = (mode: ThemeMode): void => {
  const root = document.documentElement;
  if (mode === 'auto') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', mode);
  }
  root.style.colorScheme = resolveThemeMode(mode);
};

export const useTheme = () => {
  const [mode, setMode] = useState<ThemeMode>(() => getStoredThemeMode());
  const [resolvedMode, setResolvedMode] = useState<ResolvedThemeMode>(() => resolveThemeMode(mode));

  useEffect(() => {
    applyTheme(mode);
    setResolvedMode(resolveThemeMode(mode));
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const media = window.matchMedia(DARK_MODE_QUERY);
    const handleSystemThemeChange = () => {
      if (mode !== 'auto') return;

      applyTheme('auto');
      setResolvedMode(getSystemTheme());
    };

    media.addEventListener('change', handleSystemThemeChange);
    return () => media.removeEventListener('change', handleSystemThemeChange);
  }, [mode]);

  return { mode, resolvedMode, setMode };
};
